import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Global paywall: any 402 opens the upgrade modal
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 402) {
      const detail = err.response?.data?.detail || "This is a Pro feature.";
      window.dispatchEvent(new CustomEvent("open-upgrade", { detail }));
    }
    return Promise.reject(err);
  }
);

export const mediaUrl = (path) => `${API}/media/${path}`;
export const streamUrl = (path) => `${API}/stream/${path}`;
export const shareLink = {
  clip: (id) => `${API}/share/clip/${id}`,
  show: (id, t) => `${API}/share/show/${id}${t ? `?t=${Math.floor(t)}` : ""}`,
  topic: (handle, topic) => `${API}/share/topic/${handle}/${encodeURIComponent(topic)}`,
  yana: (handle) => `${API}/share/yana/${handle}`,
};

// ---- Auth & Payments ----
export const exchangeSession = (session_id) => api.post("/auth/session", { session_id }).then((r) => r.data);
export const getMe = () => api.get("/auth/me").then((r) => r.data);
export const logout = () => api.post("/auth/logout").then((r) => r.data);
export const startCheckout = (origin_url, lookup_key = "pro_monthly") =>
  api.post("/payments/checkout", { origin_url, lookup_key }).then((r) => r.data);
export const getOpenAiKey = () => api.get("/creator/openai-key").then((r) => r.data);
export const setOpenAiKey = (key) => api.post("/creator/openai-key", { key }).then((r) => r.data);
export const deleteOpenAiKey = () => api.delete("/creator/openai-key").then((r) => r.data);
export const paymentStatus = (session_id) => api.get(`/payments/status/${session_id}`).then((r) => r.data);


export const listShows = () => api.get("/shows").then((r) => r.data);
export const getShow = (id) => api.get(`/shows/${id}`).then((r) => r.data);
export const ingestUrl = (payload) => api.post("/shows/url", payload).then((r) => r.data);
export const uploadShow = (formData, onUploadProgress) =>
  api.post("/shows/upload", formData, { onUploadProgress }).then((r) => r.data);

// Chunked upload — reliable for multi-GB / 4h+ shows
export const uploadInit = (filename) => api.post("/uploads/init", { filename }).then((r) => r.data);
export const uploadPart = (uploadId, blob) =>
  api.put(`/uploads/${uploadId}/part`, blob, { headers: { "Content-Type": "application/octet-stream" } }).then((r) => r.data);
export const uploadComplete = (uploadId, title, showDate) =>
  api.post(`/uploads/${uploadId}/complete`, { title, show_date: showDate || null }).then((r) => r.data);

export async function chunkedUpload(file, title, onProgress, showDate) {
  const { upload_id } = await uploadInit(file.name);
  const CHUNK = 8 * 1024 * 1024;
  let sent = 0;
  for (let off = 0; off < file.size; off += CHUNK) {
    const blob = file.slice(off, Math.min(off + CHUNK, file.size));
    await uploadPart(upload_id, blob);
    sent += blob.size;
    if (onProgress) onProgress(Math.round((sent / file.size) * 100));
  }
  return uploadComplete(upload_id, title || null, showDate);
}

// --- Public creator site + social sharing ---
export const getPublicCreator = (handle) => api.get(`/public/creator/${handle}`).then((r) => r.data);
export const getPublicCreatorClips = (handle, { topic, q, show_id } = {}) =>
  api.get(`/public/creator/${handle}/clips`, { params: { topic, q, show_id } }).then((r) => r.data);
export const getPublicShow = (showId) => api.get(`/public/show/${showId}`).then((r) => r.data);
export const trackReshare = (clipId, platform) => api.post(`/public/clip/${clipId}/share`, { platform }).catch(() => {});

// --- Public unified search, care/prayer catalog & resources (no auth) ---
export const publicSearch = (handle, { q, topic } = {}) =>
  api.get(`/public/creator/${handle}/search`, { params: { q, topic } }).then((r) => r.data);
export const getPublicCare = (handle, { topic, q } = {}) =>
  api.get(`/public/creator/${handle}/care`, { params: { topic, q } }).then((r) => r.data);
export const getPublicResources = (handle, { topic, q } = {}) =>
  api.get(`/public/creator/${handle}/resources`, { params: { topic, q } }).then((r) => r.data);

// --- Care catalog + resources (authed creator) ---
export const scanCare = (showId) => api.post(`/shows/${showId}/care-scan`).then((r) => r.data);
export const getCare = (showId) => api.get(`/shows/${showId}/care`).then((r) => r.data);
export const listResources = () => api.get(`/resources`).then((r) => r.data);
export const addResource = (payload) => api.post(`/resources`, payload).then((r) => r.data);
export const deleteResource = (id) => api.delete(`/resources/${id}`).then((r) => r.data);
export const seedPinelake = () => api.post(`/resources/seed-pinelake`).then((r) => r.data);

// --- Creator settings, visibility & topics (authed) ---
export const getCreatorSettings = () => api.get("/creator/settings").then((r) => r.data);
export const updateCreatorSettings = (payload) => api.patch("/creator/settings", payload).then((r) => r.data);
export const publishAll = () => api.post("/creator/publish-all").then((r) => r.data);
export const setShowVisibility = (showId, is_public) =>
  api.patch(`/shows/${showId}/visibility`, { is_public }).then((r) => r.data);
export const setClipVisibility = (clipId, is_public) =>
  api.patch(`/clips/${clipId}/visibility`, { is_public }).then((r) => r.data);
export const deleteClip = (clipId) => api.delete(`/clips/${clipId}`).then((r) => r.data);
export const setShowEra = (showId, era) => api.patch(`/shows/${showId}/era`, { era }).then((r) => r.data);
export const editShow = (showId, payload) => api.patch(`/shows/${showId}/edit`, payload).then((r) => r.data);
export const editClip = (clipId, payload) => api.patch(`/clips/${clipId}/edit`, payload).then((r) => r.data);
export const rebrandAudio = () => api.post(`/creator/rebrand-audio`).then((r) => r.data);
export const rebrandAudioStatus = () => api.get(`/creator/rebrand-audio/status`).then((r) => r.data);
export const ingestFolder = (url) => api.post(`/shows/ingest-folder`, { url, era: "tiktok" }).then((r) => r.data);
export const ingestFolderStatus = () => api.get(`/shows/ingest-folder/status`).then((r) => r.data);
export const pullMomentsAll = () => api.post(`/creator/pull-moments`).then((r) => r.data);
export const getPublicMoments = (handle) => api.get(`/public/creator/${handle}/moments`).then((r) => r.data);
export const yanaList = (handle, topic) => api.get(`/public/creator/${handle}/yana`, { params: topic ? { topic } : {} }).then((r) => r.data);
export const yanaSignup = (handle, payload) => api.post(`/public/creator/${handle}/yana/signup`, payload).then((r) => r.data);
export const yanaUploadPhoto = (handle, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/public/creator/${handle}/yana/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const yanaAdminList = () => api.get(`/yana/friends`).then((r) => r.data);
export const yanaSetVisible = (id, visible) => api.patch(`/yana/friends/${id}`, { visible }).then((r) => r.data);
export const yanaDelete = (id) => api.delete(`/yana/friends/${id}`).then((r) => r.data);
export const yanaReach = (handle, fid, payload) => api.post(`/public/creator/${handle}/yana/${fid}/reach`, payload).then((r) => r.data);
export const yanaMessages = () => api.get(`/yana/messages`).then((r) => r.data);
export const yanaMessageHandled = (mid, handled) => api.patch(`/yana/messages/${mid}`, { handled }).then((r) => r.data);
export const yanaMessageDelete = (mid) => api.delete(`/yana/messages/${mid}`).then((r) => r.data);
export const pullMomentsShow = (showId) => api.post(`/shows/${showId}/pull-moments`).then((r) => r.data);
export const backfillTopics = () => api.post("/topics/backfill").then((r) => r.data);
export const getMyTopics = () => api.get("/topics").then((r) => r.data);

// --- Semantic search, guests & chapters ---
export const archiveSearch = (query, show_id, preset) => api.post("/search", { query, show_id, preset }).then((r) => r.data);
export const makeClipFromRange = (showId, { start, end, title }) =>
  api.post(`/shows/${showId}/clip-from-range`, { start, end, title }).then((r) => r.data);
export const detectGuests = (showId) => api.post(`/shows/${showId}/detect-guests`).then((r) => r.data);
export const makeGuestClip = (showId, payload) => api.post(`/shows/${showId}/guest-clip`, payload).then((r) => r.data);
export const genChapters = (showId) => api.post(`/shows/${showId}/chapters`).then((r) => r.data);
export const getChapters = (showId) => api.get(`/shows/${showId}/chapters`).then((r) => r.data);
export const makeReel = (payload) => api.post("/reel", payload).then((r) => r.data);
export const getShowTrailer = (showId) => api.get(`/shows/${showId}/trailer`).then((r) => r.data);
export const regenShowTrailer = (showId, clip_ids) => api.post(`/shows/${showId}/trailer`, { clip_ids }).then((r) => r.data);
export const guestLeaderboard = () => api.get("/guests/leaderboard").then((r) => r.data);
export const remineShow = (id) => api.post(`/shows/${id}/remine`).then((r) => r.data);
export const deleteShow = (id) => api.delete(`/shows/${id}`).then((r) => r.data);
export const trimClip = (clipId, payload) => api.post(`/clips/${clipId}/trim`, payload).then((r) => r.data);
export const restyleClip = (clipId, payload) => api.post(`/clips/${clipId}/restyle`, payload).then((r) => r.data);
export const reframeLock = (clipId, tile) => api.post(`/clips/${clipId}/reframe-lock`, { tile }).then((r) => r.data);
export const setRoster = (showId, faces) => api.post(`/shows/${showId}/roster`, { faces }).then((r) => r.data);
export const setPreferred = (showId, payload) => api.post(`/shows/${showId}/prefer`, payload).then((r) => r.data);
export const downloadAllUrl = (showId, kind = "video") => `${API}/shows/${showId}/download-all?kind=${kind}`;
export const getProfile = () => api.get("/humor-profile").then((r) => r.data);
export const updateProfile = (payload) => api.put("/humor-profile", payload).then((r) => r.data);
export const learnProfile = () => api.post("/humor-profile/learn").then((r) => r.data);
export const getStyleDNA = () => api.get("/style-dna").then((r) => r.data);
export const ingestStyleDNA = (formData, onUploadProgress) =>
  api.post("/style-dna/ingest", formData, { onUploadProgress }).then((r) => r.data);
export const clearStyleDNA = () => api.delete("/style-dna").then((r) => r.data);
export const getDemo = () => api.get("/demo").then((r) => r.data);
export const findMoments = (showId, query, reference = false) => api.post(`/shows/${showId}/find`, { query, reference }).then((r) => r.data);
export const rateClip = (clipId, rating) => api.post(`/clips/${clipId}/rate`, { rating }).then((r) => r.data);
export const getTopPerformers = () => api.get(`/analytics/top`).then((r) => r.data);
export const clipFromRange = (showId, body) => api.post(`/shows/${showId}/clip-from-range`, body).then((r) => r.data);
export const getClipShare = (clipId) => api.get(`/clip/${clipId}/share`).then((r) => r.data);
export const registerClipView = (clipId) => api.post(`/clip/${clipId}/view`).then((r) => r.data).catch(() => {});
export const attributeSignup = (clipId) => api.post(`/auth/attribute`, { clip_id: clipId }).then((r) => r.data).catch(() => {});
export const shareUrl = (clipId) => `${window.location.origin}/c/${clipId}`;
export const getTopClips = (params) => api.get("/clips/top", { params }).then((r) => r.data);
export const retryFailed = () => api.post("/shows/retry-failed").then((r) => r.data);
export const downloadTopUrl = ({ limit = 10, min_score = 0, kind = "video" }) =>
  `${API}/clips/top/download?limit=${limit}&min_score=${min_score}&kind=${kind}`;
export const getBestTimes = () => api.get("/schedule/best-times").then((r) => r.data);
export const getSchedule = () => api.get("/schedule").then((r) => r.data);
export const createSchedule = (payload) => api.post("/schedule", payload).then((r) => r.data);
export const deleteSchedule = (sid) => api.delete(`/schedule/${sid}`).then((r) => r.data);
export const getRoster = () => api.get(`/roster`).then((r) => r.data);
export const setGlobalFollow = (name) => api.post(`/roster/follow`, { name }).then((r) => r.data);
export const renamePerson = (id, name) => api.put(`/roster/${id}`, { name }).then((r) => r.data);
export const deletePerson = (id) => api.delete(`/roster/${id}`).then((r) => r.data);
export const mergePeople = (keep_id, merge_id) => api.post(`/roster/merge`, { keep_id, merge_id }).then((r) => r.data);
export const getRosterSuggestions = () => api.get(`/roster/suggestions`).then((r) => r.data);
export const undoMerge = (tokens) => api.post(`/roster/merge/undo`, { tokens }).then((r) => r.data);
export const getPersonClips = (id) => api.get(`/roster/${id}/clips`).then((r) => r.data);
export const personClipsDownloadUrl = (id, { limit = 5, kind = "video", min_score = 0 } = {}) =>
  `${API}/roster/${id}/clips/download?limit=${limit}&kind=${kind}&min_score=${min_score}`;
